import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { SrIcon } from "@/components/ui/SrIcon";
import ActivityItem from "@/components/customers/ActivityItem";
import LogActivityDialog from "@/components/customers/LogActivityDialog";
import { useCustomerActivitiesInfinite } from "@/hooks/use-customer-activities";
import { groupActivitiesByDay } from "@/lib/customer-activity-calc";
import { formatDate } from "@/lib/format";

interface CustomerActivityTimelineProps {
  customerId: string;
  onSendEmail?: (contactId: string | null) => void;
  /** Viser bare aktiviteter på denne prosessen (uten handlingsknapper). */
  opportunityId?: string;
}

/** Kontaktloggen: tidslinje gruppert per dag (Oslo-tid), med handlinger øverst. */
export default function CustomerActivityTimeline({ customerId, onSendEmail, opportunityId }: CustomerActivityTimelineProps) {
  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = useCustomerActivitiesInfinite(customerId, 50, opportunityId);
  const [logType, setLogType] = useState<"call" | "note" | null>(null);

  const rows = data?.pages.flat() ?? [];
  const groups = groupActivitiesByDay(rows);

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
        <CardTitle className="text-base">Kontaktlogg</CardTitle>
        {!opportunityId && (
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onSendEmail?.(null)}>
              <SrIcon name="mail" className="w-3 h-3 mr-1" /> Send e-post
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setLogType("call")}>
              <SrIcon name="phone" className="w-3 h-3 mr-1" /> Logg samtale
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setLogType("note")}>
              <SrIcon name="sticky-note" className="w-3 h-3 mr-1" /> Logg notat
            </Button>
          </div>
        )}
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-24" />
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {opportunityId
              ? "Ingen aktivitet logget på denne prosessen ennå."
              : "Ingen kontakt logget ennå. Send en e-post, eller logg en samtale eller et notat for å starte historikken."}
          </p>
        ) : (
          groups.map((g) => (
            <section key={g.date} aria-label={formatDate(g.date)}>
              <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground mt-3">{formatDate(g.date)}</h3>
              <ul>
                {g.items.map((row) => (
                  <ActivityItem key={row.id} row={row} />
                ))}
              </ul>
            </section>
          ))
        )}
        {hasNextPage && (
          <Button variant="outline" size="sm" className="mt-3" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
            {isFetchingNextPage ? "Henter ..." : "Vis mer"}
          </Button>
        )}
      </CardContent>
      <LogActivityDialog
        open={logType !== null}
        onOpenChange={(o) => !o && setLogType(null)}
        customerId={customerId}
        type={logType ?? "call"}
      />
    </Card>
  );
}
